import { useState } from 'react'
import { useLazyQuery } from 'components/graphql'
import { listClosings } from '../gql'

const useClosingSum = ({ variables }, callback) => {
	const params = { ...variables.params, limit: 0, cursors: null }
	const filter = { ...variables.filter, inactive: false }

	const [isSum, setIsSum] = useState(false)
	const [ loadSum, { data, loading } ] = useLazyQuery(listClosings, {
		variables: { ...variables, filter, params },
		onCompleted: () => callback && callback()
	})

	const sum = (a, b) => (a * 10000 + b * 10000) / 10000

	const getAllSalesGroup = (initArray) => {
		if(!initArray) {
			return []
		}
		const salesGroupsArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.salesGroups)) {
				return resArr.concat(closing.salesGroups)
			}
			return resArr
		}, [])
		const salesGroupsMap = salesGroupsArr.reduce((total, sg) => {
			const sgId = sg?.salesGroup?.id
			if(typeof total[sgId] === 'undefined') {
				total[sgId] = { ...sg }
				return total
			}
			total[sgId].amount = sum(total[sgId].amount, sg.amount)
			total[sgId].discount = sum(total[sgId].discount, sg.discount)
			total[sgId].mWStFree = sum(total[sgId].mWStFree, sg.mWStFree)
			total[sgId].mWStNormal = sum(total[sgId].mWStNormal, sg.mWStNormal)
			total[sgId].mWStReduced = sum(total[sgId].mWStReduced, sg.mWStReduced)
			total[sgId].mWStSpecial = sum(total[sgId].mWStSpecial, sg.mWStSpecial)
			total[sgId].reduction = sum(total[sgId].reduction, sg.reduction)
			total[sgId].total = sum(total[sgId].total, sg.total)
			return total
		}, {})
		return Object.values(salesGroupsMap)
	}

	const getAllUsersData = (initArray) => {
		if(!initArray) {
			return []
		}
		const usersArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.users)) {
				return resArr.concat(closing.users)
			}
			return resArr
		}, [])

		let totalBillCount = 0
		let totalAmount = 0
		const usersMap = usersArr.reduce((map, userRecord) => {
			if (!userRecord?.user?.id) {
				return map
			}

			const userId = userRecord.user.id
			if(typeof map[userId] === 'undefined') {
				map[userId] = { ...userRecord }
				return map
			}
			totalBillCount = totalBillCount + userRecord.clientCount
			totalAmount = sum(totalAmount, userRecord.total)

			map[userId].total = sum(map[userId].total, userRecord.total)
			map[userId].clientCount = sum(map[userId].clientCount, userRecord.clientCount)
			map[userId].cancellationsCount = sum(map[userId].cancellationsCount, userRecord.cancellationsCount)
			map[userId].cancellationsAmount = sum(map[userId].cancellationsAmount, userRecord.cancellationsAmount)
			map[userId].buttonOpenCount = sum(map[userId].buttonOpenCount, userRecord.buttonOpenCount)
			map[userId].buttonBackCount = sum(map[userId].buttonBackCount, userRecord.buttonBackCount)
			map[userId].buttonDelCount = sum(map[userId].buttonDelCount, userRecord.buttonDelCount)
			map[userId].buttonDelAmount = sum(map[userId].buttonDelAmount, userRecord.buttonDelAmount)
			map[userId].inCafeCount = sum(map[userId].inCafeCount, userRecord.inCafeCount)
			map[userId].inCafeAmount = sum(map[userId].inCafeAmount, userRecord.inCafeAmount)
			map[userId].tipsTotal = sum(map[userId].tipsTotal, userRecord.tipsTotal)

			return map
		}, {})
		const totalAverageBill = totalBillCount ? totalAmount / totalBillCount : null
		return Object.values(usersMap).map(user => {
			const averageBill = user.clientCount ? user.total / user.clientCount : null
			return {
				...user,
				averageBill,
				userEfficiency: averageBill ? (averageBill / totalAverageBill * 100).toFixed(1) : null,
				inCafePercent: user.total ? (user.inCafeAmount * 100 / user.total).toFixed(1) : null
			}
		})
	}

	const getAllCurrencyData = (initArray) => {
		if(!initArray) {
			return []
		}
		const currencyArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.currency)) {
				return resArr.concat(closing.currency)
			}
			return resArr
		}, [])
		const currencyMap = currencyArr.reduce((total, currency) => {
			if (!currency?.name) {
				return total
			}

			const currencyName = currency.name
			if(typeof total[currencyName] === 'undefined') {
				total[currencyName] = { ...currency }
				return total
			}
			total[currencyName].present = sum(total[currencyName].present || 0, currency.present || 0)
			total[currencyName].exchangeRate = currency.exchangeRate // Keep the same exchange rate
			total[currencyName].presentConverted = sum(total[currencyName].presentConverted || 0, currency.presentConverted || 0)
			total[currencyName].cashInTransit = sum(total[currencyName].cashInTransit || 0, currency.cashInTransit || 0)
			total[currencyName].difference = sum(total[currencyName].difference || 0, currency.difference || 0)
			total[currencyName].amount = sum(total[currencyName].amount || 0, currency.amount || 0)
			total[currencyName].amountConverted = sum(total[currencyName].amountConverted || 0, currency.amountConverted || 0)
			total[currencyName].stock_amount = sum(total[currencyName].stock_amount || 0, currency.stock_amount || 0)
			return total
		}, {})
		return Object.values(currencyMap)
	}

	const getAllPaymentMethodsData = (initArray) => {
		if(!initArray) {
			return []
		}
		const paymentMethodsArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.paymentMethods)) {
				return resArr.concat(closing.paymentMethods)
			}
			return resArr
		}, [])
		const paymentMethodsMap = paymentMethodsArr.reduce((total, pm) => {
			if (!pm?.name) {
				return total
			}

			const pmName = pm.name
			if(typeof total[pmName] === 'undefined') {
				total[pmName] = { ...pm }
				return total
			}
			total[pmName].count = sum(total[pmName].count || 0, pm.count || 0)
			total[pmName].amount = sum(total[pmName].amount || 0, pm.amount || 0)
			return total
		}, {})
		return Object.values(paymentMethodsMap)
	}

	const getAllCardSchemesData = (initArray) => {
		if(!initArray) {
			return []
		}
		const cardSchemesArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.cardSchemes)) {
				return resArr.concat(closing.cardSchemes)
			}
			return resArr
		}, [])
		const cardSchemesMap = cardSchemesArr.reduce((total, cs) => {
			if (!cs?.name) {
				return total
			}

			const csName = cs.name
			if(typeof total[csName] === 'undefined') {
				total[csName] = { ...cs }
				return total
			}
			total[csName].count = sum(total[csName].count || 0, cs.count || 0)
			total[csName].amount = sum(total[csName].amount || 0, cs.amount || 0)
			return total
		}, {})
		return Object.values(cardSchemesMap)
	}

	const getAllCouponsData = (initArray) => {
		if(!initArray) {
			return []
		}
		const couponsArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.coupons)) {
				return resArr.concat(closing.coupons)
			}
			return resArr
		}, [])
		const couponsMap = couponsArr.reduce((total, coupon) => {
			if (!coupon?.name) {
				return total
			}

			const couponName = coupon.name
			if(typeof total[couponName] === 'undefined') {
				total[couponName] = { ...coupon }
				return total
			}
			total[couponName].amount = sum(total[couponName].amount || 0, coupon.amount || 0)
			return total
		}, {})
		return Object.values(couponsMap)
	}

	const getAllCashbookData = (initArray) => {
		if(!initArray) {
			return []
		}
		const cashbookArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.cashbook)) {
				return resArr.concat(closing.cashbook)
			}
			return resArr
		}, [])
		const cashbookMap = cashbookArr.reduce((total, cb) => {
			if (!cb?.description) {
				return total
			}

			const cbDescription = cb.description
			if(typeof total[cbDescription] === 'undefined') {
				total[cbDescription] = { ...cb }
				return total
			}
			total[cbDescription].have = sum(total[cbDescription].have || 0, cb.have || 0)
			total[cbDescription].should = sum(total[cbDescription].should || 0, cb.should || 0)
			return total
		}, {})
		return Object.values(cashbookMap)
	}

	const getAllSpecialSalesData = (initArray) => {
		if(!initArray) {
			return []
		}
		const specialSalesArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.specialSales)) {
				return resArr.concat(closing.specialSales)
			}
			return resArr
		}, [])
		const specialSalesMap = specialSalesArr.reduce((total, ss) => {
			if (!ss?.name) {
				return total
			}

			const ssName = ss.name
			if(typeof total[ssName] === 'undefined') {
				total[ssName] = { ...ss }
				return total
			}
			total[ssName].amount = sum(total[ssName].amount || 0, ss.amount || 0)
			return total
		}, {})
		return Object.values(specialSalesMap)
	}

	const getAllTaxesData = (initArray) => {
		if(!initArray) {
			return []
		}
		const taxesArr = initArray.reduce((resArr, closing) => {
			if (Array.isArray(closing.taxes)) {
				return resArr.concat(closing.taxes)
			}
			return resArr
		}, [])
		const taxesMap = taxesArr.reduce((total, tax) => {
			if (!tax?.percent && tax?.percent !== 0) {
				return total
			}

			const taxPercent = tax.percent
			if(typeof total[taxPercent] === 'undefined') {
				total[taxPercent] = { ...tax }
				return total
			}
			total[taxPercent].sales = sum(total[taxPercent].sales || 0, tax.sales || 0)
			total[taxPercent].MWSt = sum(total[taxPercent].MWSt || 0, tax.MWSt || 0)
			total[taxPercent].total = sum(total[taxPercent].total || 0, tax.total || 0)
			return total
		}, {})
		return Object.values(taxesMap)
	}

	const getAllStatisticsData = (initArray) => {
		if(!initArray) {
			return {}
		}

		const isNullOrUndefined = (value) => value === null || typeof value === 'undefined'

		const aggregatedStats = initArray.reduce((stats, closing) => {
			stats.total = sum(stats.total || 0, closing.total || 0)
			stats.clientCount = sum(stats.clientCount || 0, closing.clientCount || 0)
			stats.productCount = sum(stats.productCount || 0, closing.productCount || 0)
			stats.cancellationsCount = sum(stats.cancellationsCount || 0, closing.cancellationsCount || 0)
			stats.clientCardValue = sum(stats.clientCardValue || 0, closing.clientCardValue || 0)
			stats.doorCounter = sum(stats.doorCounter || 0, closing.doorCounter || 0)
			stats.guestsCount = sum(stats.guestsCount || 0, closing.guestsCount || 0)

			if (!isNullOrUndefined(closing.billNumStart)) {
				if (isNullOrUndefined(stats.billNumStart)) {
					stats.billNumStart = closing.billNumStart
				} else {
					stats.billNumStart = Math.min(stats.billNumStart, closing.billNumStart)
				}
			}

			if (!isNullOrUndefined(closing.billNumEnd)) {
				if (isNullOrUndefined(stats.billNumEnd)) {
					stats.billNumEnd = closing.billNumEnd
				} else {
					stats.billNumEnd = Math.max(stats.billNumEnd, closing.billNumEnd)
				}
			}

			if (closing.dateInterval?.from) {
				const closingFrom = new Date(closing.dateInterval.from)
				if (!stats.dateInterval?.from || new Date(stats.dateInterval.from) > closingFrom) {
					stats.dateInterval = {
						...stats.dateInterval,
						from: closing.dateInterval.from
					}
				}
			}

			if (closing.dateInterval?.to) {
				const closingTo = new Date(closing.dateInterval.to)
				if (!stats.dateInterval?.to || new Date(stats.dateInterval.to) < closingTo) {
					stats.dateInterval = {
						...stats.dateInterval,
						to: closing.dateInterval.to
					}
				}
			}

			if (closing.moneyCountDateTime) {
				const closingMoneyCount = new Date(closing.moneyCountDateTime)
				if (!stats.moneyCountDateTime || new Date(stats.moneyCountDateTime) < closingMoneyCount) {
					stats.moneyCountDateTime = closing.moneyCountDateTime
				}
			}

			return stats
		}, {
			total: 0,
			clientCount: 0,
			productCount: 0,
			cancellationsCount: 0,
			clientCardValue: 0,
			billNumStart: null,
			billNumEnd: null,
			doorCounter: 0,
			guestsCount: 0,
			dateInterval: { from: null, to: null },
			moneyCountDateTime: null
		})

		return aggregatedStats
	}

	return {
		toggleSum: () => {
			if(!isSum) {
				setIsSum(true)
				loadSum()
			} else {
				setIsSum(false)
			}
		},
		isSum,
		loading,
		data: {
			salesGroups: getAllSalesGroup(data.listClosings?.items),
			users: getAllUsersData(data.listClosings?.items),
			currency: getAllCurrencyData(data.listClosings?.items),
			paymentMethods: getAllPaymentMethodsData(data.listClosings?.items),
			cardSchemes: getAllCardSchemesData(data.listClosings?.items),
			coupons: getAllCouponsData(data.listClosings?.items),
			cashbook: getAllCashbookData(data.listClosings?.items),
			specialSales: getAllSpecialSalesData(data.listClosings?.items),
			taxes: getAllTaxesData(data.listClosings?.items),
			statistics: getAllStatisticsData(data.listClosings?.items)
		}
	}
}

export default useClosingSum
